import { z } from "zod";
import { needsTermsAcceptance } from "./terms";

/**
 * A **provider's or catalog's own** terms-of-usage declaration (docs/297-PROVIDER-
 * CATALOG-TERMS-ACCEPTANCE.md v0) — distinct from `legal/terms.ts`'s **app-wide**
 * Terms of Use (docs/077). A content provider (docs/094/263/266) or a specific catalog
 * may require the user to accept its OWN licensing/acceptable-use terms before using
 * its content, separate from — and possibly in addition to — the app's own ToU.
 *
 * `version` is a plain integer, exactly like `CURRENT_TERMS_VERSION` — bumping it forces
 * every user who already accepted an older version to re-accept (`needsTermsAcceptance`,
 * reused unchanged). `url`/`text` are both optional so an author can point at a hosted
 * document, ship a short inline policy, or (rarely) neither — the acceptance gate still
 * fires on `version`/`required` alone in that case, showing only `title` (or a generic
 * fallback) with no link/body to read (an edge case, not the common path).
 */
export interface CatalogTerms {
  url?: string;
  text?: string;
  version: number;
  title?: string;
  /**
   * Whether accepting this blocks use of the scoped content (`true`/omitted, the
   * default — see {@link isTermsRequired}) or is purely informational — a "View terms"
   * link with no block (`false`). A provider/catalog author who bothers to declare
   * `terms` is presumed to mean it, so the SAFE default when this key is simply absent
   * is `required` (never accidentally skip a real terms block); set `required: false`
   * explicitly to make a declaration display-only.
   */
  required?: boolean;
}

export const CatalogTermsSchema = z.object({
  url: z.string().url().optional(),
  text: z.string().optional(),
  version: z.number(),
  title: z.string().optional(),
  required: z.boolean().optional(),
});

/** `terms.required !== false` — see {@link CatalogTerms.required}'s own doc comment for
 * why absent defaults to required. Pure. */
export function isTermsRequired(terms: CatalogTerms): boolean {
  return terms.required !== false;
}

/** A single most-recently-accepted-version record for one scope (see {@link
 * providerTermsScope}/{@link catalogTermsScope}/{@link packTermsScope}). Mirrors docs/
 * 077's `Settings.acceptedTermsVersion`/`acceptedTermsAt` pair, but keyed by scope
 * instead of being the app's one global field. */
export interface AcceptedTermsRecord {
  version: number;
  acceptedAt: number;
}

/**
 * Scope-id builders (docs/297 §3.2) — a plain, opaque `provider:<id>` / `catalog:<id>` /
 * `pack:<id>` string, always built from a **canonical** id (docs/055-STORED-REFS-
 * CANONICAL-IDS.md: a provider's stable local id, a catalog's own `Catalog.id`, or a
 * pack's own `CatalogPackEntry.id` — never a tree `path`, which can change under a
 * cross-listed/reorganized catalog). Never parsed back apart — purely a storage key +
 * an `AcceptedTermsStore` lookup key.
 */
export function providerTermsScope(providerId: string): string {
  return `provider:${providerId}`;
}
export function catalogTermsScope(catalogId: string): string {
  return `catalog:${catalogId}`;
}
export function packTermsScope(packId: string): string {
  return `pack:${packId}`;
}

const PROVIDER_SCOPE_PREFIX = "provider:";

/**
 * The inverse of {@link providerTermsScope} — extracts the provider id from a
 * scope string IF it's a provider-scoped one, else `null`. A deliberate, single,
 * well-documented exception to this module's own "never parsed back apart" rule
 * (see the module doc above) — needed so the accept-terms screen
 * (`app/accept-catalog-terms.tsx`, docs/312-PROVIDER-LEGAL-AND-TERMS-EDITING.md
 * v0 §9) can look up which signed-in provider's `POST /accept-terms` to call
 * after a local accept. Never throws.
 */
export function parseProviderTermsScope(scope: string): string | null {
  return scope.startsWith(PROVIDER_SCOPE_PREFIX) ? scope.slice(PROVIDER_SCOPE_PREFIX.length) : null;
}

/** One scope's terms declaration, paired with the scope id it's keyed under — the unit
 * `termsToAccept` filters and the accept screen is handed to render/record against. */
export interface ScopedTerms {
  scope: string;
  terms: CatalogTerms;
}

/**
 * The list of scopes whose terms are **required** and **not yet accepted at the current
 * version** (docs/297 §3.2) — drives the accept-terms prompt; an empty result means
 * nothing is blocking. `accepted` is the FULL map from `AcceptedTermsStore.getAll()`
 * (or an equivalent snapshot) — a scope absent from it is treated as "never accepted"
 * (`needsTermsAcceptance`'s own `null`/`undefined` handling). A `required: false`
 * candidate is always excluded — it's informational only, never blocking, so it never
 * appears here (see `isTermsRequired`). Pure; order-preserving (the caller's own
 * candidate order, e.g. provider before catalog).
 */
export function termsToAccept(
  candidates: ScopedTerms[],
  accepted: Record<string, AcceptedTermsRecord | undefined>,
): ScopedTerms[] {
  return candidates.filter(
    (c) => isTermsRequired(c.terms) && needsTermsAcceptance(accepted[c.scope]?.version, c.terms.version),
  );
}
